import type { HeadersFunction, LinksFunction, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import {
  data,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "@remix-run/react";
import { rootAuthLoader } from "@clerk/remix/ssr.server";
import { ClerkApp } from "@clerk/remix";
import { dark } from '@clerk/themes'

import { ClientHintCheck, getHints } from './lib/client-hints'
import { getToast } from '~/lib/toast.server'

import okStylesheet from "@okbrk/ui/tailwind.css?url";
import fontStylesheet from "./styles/fonts.css?url";
import { ThemeProvider, useTheme, PreventFlashOnWrongTheme, Theme, } from 'remix-themes'

import { themeSessionResolver } from './services/theme-session'
import { CommonErrorBoundary } from '~/components/error-boundary';
import { combineHeaders, getDomainUrl } from '~/lib/misc';
import { makeTimings } from '~/lib/timing.server';
import { useNonce } from '~/lib/nonce-provider';
import { withSentry } from '@sentry/remix';
import { Toaster } from '@okbrk/ui/components/ui/sonner'
import { toast } from 'sonner'
import { ProgressBar } from '~/components/progress-bar';
import { createUser, getUser } from '~/services/auth';

export const links: LinksFunction = () => {
  return [
    { rel: "stylesheet", href: fontStylesheet },
    { rel: "stylesheet", href: okStylesheet },
    process.env.NODE_ENV === 'production' ? { rel: "icon", type: "image/x-icon", href: "/favicon.ico" } : { rel: "icon", type: "image/x-icon", href: "/dev-favicon.ico" },
  ];
};

export const meta: MetaFunction = () => {
  const allowIndexing = false
  return [
    { charset: "utf-8" },
    { title: "thefaqapp" },
    { name: "description", content: "thefaqapp" },
    { viewport: "width=device-width,initial-scale=1" },
    { name: "robots", content: allowIndexing ? "index, follow" : "noindex, nofollow" },
  ];
};


export async function loader(args: LoaderFunctionArgs) {
  const timings = makeTimings('root loader')
  const { toast, headers: toastHeaders } = await getToast(args.request)


  const modifiedHeaders = combineHeaders(
    { 'Server-Timing': timings.toString() },
    toastHeaders,
  )

  return rootAuthLoader(args, async ({ request }) => {
    const { getTheme } = await themeSessionResolver(request);
    const { sessionId, userId, getToken, sessionClaims } = request.auth;

    if (userId) {
      const user = await getUser(userId);
      if (!user) {
        // await createUser({ clerkId: userId, name });
      }
    }

    return {
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(),
        },
      },
      toast,
    }
  });
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
  }
  return headers
}


function Document({
  children,
  nonce,
}: {
  children: React.ReactNode
  nonce: string
}) {
  const [_theme] = useTheme()

  return (
    <html lang="en" data-theme={_theme} className='h-full overflow-x-hidden' suppressHydrationWarning>
      <head>
        <PreventFlashOnWrongTheme ssrTheme={Boolean(_theme)} />
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <Links />
      </head>
      <body className="bg-background text-foreground">
        {children}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  )
}

export function Layout({ children }: { children: React.ReactNode }) {
  // if there was an error running the loader, data could be missing
  const data = useLoaderData<typeof loader>()
  const nonce = useNonce()
  const theme = data.requestInfo.userPrefs.theme

  return (
    <ThemeProvider specifiedTheme={theme} themeAction='/resources/theme-switch'>
      <Document nonce={nonce}>
        {children}
      </Document>
    </ThemeProvider>
  )
}

function App() {
  // const data = useLoaderData<typeof loader>()
  // const matches = useMatches()
  // if (data.toast) {
  //   toast(data.toast.)
  // }

  return (
    <>
      <Outlet />

      <Toaster />
      <ProgressBar />
    </>

  );
}

function AppWithProviders() {
  return (
    <>
      <App />
    </>
  );
}

export default () => {
  const [theme] = useTheme()
  return withSentry(ClerkApp(AppWithProviders, {
    appearance: {
      baseTheme: theme === Theme.DARK ? dark : undefined,
    }
  }))();
}

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = CommonErrorBoundary;